import { Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSWR, { SWRResponse, mutate as globalMutate } from 'swr';

import { debounce } from 'lodash';
import { ContactManagerHeader } from '@modules/ContactManager/components/Header';
import { getOptions, queryParams, withQueryParams } from '@shared/commonFunctions';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { Modal } from '@shared/components/modals/Modal';
import { SERVICES } from '@shared/constants/apiConfig';
import { Roles } from '@shared/constants/constants';
import { useQueryState } from '@shared/hooks/useQueryState';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { useUsers } from '@shared/hooks/useUsers';
import { IAllUsers, IMap, IOption } from '@shared/interfaces';
import { PageWrapper } from '@shared/styledComponents';
import { SidebarWrapper } from '@styles/ContactManager/TabsStyles';
import { ContactManagerWrapper } from '@styles/StyledDiv';

import { useTabs } from '@shared/hooks/useTabs';
import { useAppSelector } from '@store/hooks';
import { getDealQueueCount, selectViewAsUserId, getLeadQueueCount } from '@store/reducers/headerSlice';
import { ContactManagerContentWraper } from '@styles/CommonStyles';
import { sortArray } from '@shared/utils/helpers';
import { DetailedCreditReport } from './components/Header/components/DetailedCreditReport';
import { CreditBureauScoreType, EBureauType, IContactHeaderData, ILeadData, TabIndexEnum } from './components/Header/interfaces';
import { ContactManagerSidebar } from './components/Sidebar';
import { ContactManagerTabs } from './components/Tabs';
import { IAsset } from './components/Tabs/components/Assets/interfaces';
import { ContactAlerts } from './components/Tabs/components/Contact/ContactAlerts';
import { NContact } from './components/Tabs/components/Contact/validation';
import { DealUsers } from './components/Tabs/components/Deals/components/DealUsers';
import { IDeal, IDealSummary } from './components/Tabs/components/Deals/interfaces';
import { ILead } from './components/Tabs/components/Leads/types/interfaces/interfaces';
import { ContactUserBlock } from './components/Tabs/shared/ContactUserBlock/ContactUserBlock';
import { requestGetQueueData } from './components/Header/requests';
import { IDropdownPropsWithMutation, IDropdownUsersProps, roleByName, roleByNameUrl } from './interfaces';
import { FetchDataOptions } from './types';

const HUBSPOT_PRIMARY_SOURCE = 'HUBSPOT';

export const mutateRolesByName = () =>
  roleByName.map((name) => globalMutate(`${withQueryParams(roleByNameUrl, { name }, false)}`, undefined, { revalidate: false }));

const generateUserSelectOptions = (users: IAllUsers | undefined) => {
  const userSelectOptions = (users?.rolesUsers ?? [])
    .filter((user) => !user.isExcluded)
    .map((user) => ({
      label: user.userName,
      value: user.userId,
    }));
  return sortArray(userSelectOptions, 'label', 'asc');
};

const generateUserSelectOptionsByRole = (
  admin: boolean,
  userSelectRolesOptions: IDropdownUsersProps,
  userSelectAllOptions: IOption[]
): IDropdownPropsWithMutation => {
  const options = admin ? { userSelectOptions: userSelectAllOptions } : { userSelectOptions: userSelectRolesOptions };

  return options;
};

export const ContactManager = () => {
  const location = useLocation();
  const { isAdmin, userId, userHasRoles } = useUserAccount();

  const canPullAll = isAdmin || userHasRoles([Roles.CREDIT_REPORTS_PULL_ALL]);
  const canPullExperian = userHasRoles([Roles.CREDIT_REPORTS_PULL_EXPERIAN]);
  const canPullTransunion = userHasRoles([Roles.CREDIT_REPORTS_PULL_TRANSUNION]);
  const canPullEquifax = userHasRoles([Roles.CREDIT_REPORTS_PULL_EQUIFAX]);
  const canViewAll = userHasRoles([Roles.CREDIT_REPORTS_VIEW_ALL]);

  const param = queryParams();
  const navigate = useNavigate();
  const viewAs = useAppSelector(selectViewAsUserId);
  const { openTab, updateCurrentTab } = useTabs();
  const [tabQuery] = useQueryState('tab');
  const [contactIdQuery] = useQueryState('id');
  const [dealIdQuery, setDealIdQuery] = useQueryState('dealId');
  const [assetIdQuery, setAssetIdQuery] = useQueryState('assetId');
  const [firstLoad, setFirstLoadStatus] = useState(true);
  const [selectedLeadId, selectLeadId] = useQueryState('leadId');
  const [contactAlertsKey, setContactAlertsKey] = useState(0);
  const [creditReports, setCreditReports] = useState<IMap<number>>({});
  const [pulledFicoTypes, setPulledFicoTypes] = useState<Partial<Record<EBureauType, CreditBureauScoreType[]>>>({});
  const [currentRecord, setCurrentRecord] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dealQueueLength = useAppSelector(getDealQueueCount);
  const leadQueueLength = useAppSelector(getLeadQueueCount);

  const currentTab = Number(tabQuery) || 0;

  const {
    data: contactData,
    isLoading: isContactDataLoading,
    mutate: reloadContact,
  } = useSWR<NContact.Entity>(contactIdQuery && `/${SERVICES.CONTACT}/contacts/${contactIdQuery}`);

  const { data: contactDetailsData, isLoading: contactDetailsLoading } = useSWR<IContactHeaderData>(
    contactIdQuery && `/${SERVICES.CONTACT}/contacts/${contactIdQuery}/header`
  );

  const {
    data: leads = [],
    isLoading: isLeadsLoading,
    mutate: reloadLeads,
  } = useSWR<ILead[]>(contactIdQuery && `/${SERVICES.LEAD}/contacts/${contactIdQuery}/leads`, {
    revalidateOnMount: true,
  });
  const {
    data: deals = [],
    error: dealsSummaryError,
    isValidating: validatingDeals,
    isLoading: isDealsLoading,
    mutate: mutateDeals,
  } = useSWR<IDealSummary[]>(contactIdQuery && `/${SERVICES.DEAL}/contacts/${contactIdQuery}/deals/summary`, {
    revalidateOnMount: true,
  });
  const { data: assets = [], isLoading: isAssetsLoading, mutate: mutateAssets, } = useSWR<IAsset[]>(
    contactIdQuery && `/${SERVICES.ASSET}/contacts/${contactIdQuery}/assets`,
    {
      revalidateOnMount: true,
    }
  );
  const { data: deal, mutate: mutateDeal } = useSWR<IDeal>(dealIdQuery && `/${SERVICES.DEAL}/deals/${dealIdQuery}`);

  const {
    data: leadData,
    mutate: reloadLeadData,
    isLoading: leadLoading,
  } = useSWR<ILeadData>(selectedLeadId && `/${SERVICES.APICOMPOSER}/v1/leads/${selectedLeadId}`);

  useEffect(() => {
    if (dealIdQuery?.trim() && deals.findIndex((r) => r.dealId == dealIdQuery) < 0) {
      mutateDeals();
    }
  }, [dealIdQuery]);

  const getQueueData = useCallback(
    async (recordNumber: number) => {
      setLoading(true);
      const queueType = param?.isDealsQueue === 'true' ? 'dealqueuebyid' : 'leadqueuebyid';
      const url = `${SERVICES.SEARCH}/search/${queueType}`;
      const orderBy = param?.orderBy;
      const orderByDirection = param?.orderByDirection;

      const data = await requestGetQueueData(url, recordNumber, param?.queueId, viewAs.userId, orderBy, orderByDirection);
      setLoading(false);

      return data;
    },
    [viewAs, param?.queueId, param?.isDealsQueue]
  );

  const { users } = useUsers();

  const fetchUsers = useCallback(
    (options: FetchDataOptions): SWRResponse<IAllUsers> =>
      useSWR<IAllUsers>(
        userId &&
        !isAdmin &&
        options.isAllowed &&
        withQueryParams(`/${options.service}/roleAssignment/roleByName`, { name: options.roleName }, false),
        { revalidateOnMount: undefined }
      ),
    [userId, isAdmin]
  );

  const { data: sdrUsers } = fetchUsers({ service: SERVICES.USER, roleName: Roles.SDR_DEAL_USER, isAllowed: true });
  const { data: lcUsers } = fetchUsers({ service: SERVICES.USER, roleName: Roles.LC, isAllowed: true });
  const { data: lcaUsers } = fetchUsers({
    service: SERVICES.USER,
    roleName: Roles.LCA,
    isAllowed: param?.tab == TabIndexEnum.Deals,
  });
  const { data: bankUsers } = fetchUsers({
    service: SERVICES.USER,
    roleName: Roles.BANK,
    isAllowed: param?.tab == TabIndexEnum.Deals,
  });
  const { data: lpUsers } = fetchUsers({
    service: SERVICES.USER,
    roleName: Roles.LP,
    isAllowed: param?.tab == TabIndexEnum.Deals,
  });
  const { data: accountingUsers } = fetchUsers({
    service: SERVICES.USER,
    roleName: Roles.ACCOUNING,
    isAllowed: param?.tab == TabIndexEnum.Deals,
  });
  const { data: titlesUsers } = fetchUsers({
    service: SERVICES.USER,
    roleName: Roles.TITLES,
    isAllowed: param?.tab == TabIndexEnum.Deals,
  });

  const sdrUserSelectOptions = useMemo(() => generateUserSelectOptions(sdrUsers), [sdrUsers]);
  const lcaUserSelectOptions = useMemo(() => generateUserSelectOptions(lcaUsers), [lcaUsers]);
  const lcUserSelectOptions = useMemo(() => generateUserSelectOptions(lcUsers), [lcUsers]);
  const bankUserSelectOptions = useMemo(() => generateUserSelectOptions(bankUsers), [bankUsers]);
  const lpUsersUserSelectOptions = useMemo(() => generateUserSelectOptions(lpUsers), [lpUsers]);
  const accountingUsersUserSelectOptions = useMemo(() => generateUserSelectOptions(accountingUsers), [accountingUsers]);
  const titlesUsersUserSelectOptions = useMemo(() => generateUserSelectOptions(titlesUsers), [titlesUsers]);

  const userSelectRolesOptions = {
    sdrUserSelectOptions,
    lcaUserSelectOptions,
    lcUserSelectOptions,
    bankUserSelectOptions,
    lpUsersUserSelectOptions,
    accountingUsersUserSelectOptions,
    titlesUsersUserSelectOptions,
  };

  const userSelectAllOptions = useMemo(() => getOptions(users?.groupUsers ?? [], 'userId', 'userName'), [users?.groupUsers]);
  const { userSelectOptions } = generateUserSelectOptionsByRole(isAdmin, userSelectRolesOptions, userSelectAllOptions);

  const disableArrowButtons = useMemo(() => !param?.recordNumber && !param?.queueId, [param?.recordNumber, param?.queueId]);

  const displayAdditionalInfo = useMemo(
    () => (leads?.find((lead) => lead.primarySourceName?.includes(HUBSPOT_PRIMARY_SOURCE)) ? true : false),
    [leads]
  );

  useEffect(() => {
    const getTotalCount = async () => {
      const data = await getQueueData(Number(param?.recordNumber));
      setTotalCount(data?.totalCount ?? 0);
    };

    if (param?.recordNumber) {
      getTotalCount();
    }
  }, []);

  const initialPickedDeal = useMemo(() => deals.find(d => d.isPicked), [deals]);
  const initialLead = useMemo(() => leads?.[0], [leads]);
  const initialAsset = useMemo(() => assets?.[0], [assets]);

  const handleSelectAsset = useCallback((selectedAssetId?: string) => {
    setAssetIdQuery(selectedAssetId);
  }, []);

  const handleSelectDeal = useCallback((selectedDealId?: string) => {
    setDealIdQuery(selectedDealId);
  }, []);

  useEffect(() => {
    if (!firstLoad || isLeadsLoading || isDealsLoading || isAssetsLoading) {
      return;
    }

    const tab = param?.tab;

    const setDealAndAssociations = (deal: IDealSummary) => {
      if (deal) {
        handleSelectDeal(deal.dealId);
        handleSelectAsset(deal.assetId);
        selectLeadId(deal.leadId);
      }
    };

    switch (tab) {
      case TabIndexEnum.Deals.toString():
        if (!dealIdQuery && initialPickedDeal) {
          setDealAndAssociations(initialPickedDeal);
        }
        break;

      case TabIndexEnum.Leads.toString():
        if (!selectedLeadId && initialLead) {
          selectLeadId(initialLead.id);
          handleSelectAsset(initialLead.assetId);
        }
        break;

      case TabIndexEnum.Assets.toString():
        if (!assetIdQuery && initialAsset) {
          handleSelectAsset(initialAsset.id);
          if (initialPickedDeal) {
            handleSelectDeal(initialPickedDeal.dealId);
          } else if (initialLead) {
            selectLeadId(initialLead.id);
          }
        }
        break;

      default:
        if (initialPickedDeal) {
          setDealAndAssociations(initialPickedDeal);
        } else if (initialLead && !selectedLeadId) {
          selectLeadId(initialLead.id);
          handleSelectAsset(initialLead.assetId);
        } else if (initialAsset && !assetIdQuery) {
          handleSelectAsset(initialAsset.id);
        }
    }

    setFirstLoadStatus(false);

  }, [
    firstLoad,
    isLeadsLoading,
    isDealsLoading,
    isAssetsLoading,
    param?.tab,
    dealIdQuery,
    selectedLeadId,
    assetIdQuery,
    initialPickedDeal,
    initialLead,
    initialAsset,
    handleSelectDeal,
    handleSelectAsset,
    selectLeadId,
  ]);

  useEffect(() => {
    contactData?.id && contactData?.employments && reloadContactAlerts();
  }, [contactData?.id, contactData?.employments]);
  
  const debouncedUpdateTab = useCallback(
    debounce((tabData) => {
      updateCurrentTab(tabData);
    }, 150),
    [updateCurrentTab]
  );

  const handleArrowButtonClick = useCallback(
    async (isLeftClicked = false) => {
      if (!param.queueId) {
        return;
      }

      const queueType = param?.isDealsQueue === 'true' ? 'dealqueuebyid' : 'leadqueuebyid';
      const totalRecords = queueType === 'dealqueuebyid' ? dealQueueLength : leadQueueLength;

      //check the current index
      const currentIndex = Math.max(Number(param?.recordNumber || 1) - 1, 0);

      //update the new index based on left or right  click
      let newIndex = isLeftClicked ? currentIndex - 1 : currentIndex + 1;

      //check for valid range
      if (newIndex < 0) newIndex = 0;
      if (newIndex >= totalRecords) newIndex = totalRecords - 1;

      const data = await getQueueData(newIndex + 1);
      const finalData = data?.items?.[0];
      if (!finalData) return;
      setTotalCount(data?.totalCount ?? 0);

      const route = withQueryParams('/contact-manager', {
        id: finalData?.contactId,
        dealId: finalData?.dealId,
        leadId: finalData?.leadId,
        tab: param.tab,
        queueId: param.queueId,
        recordNumber: newIndex + 1,
      });

      setCurrentRecord(newIndex);
      const title = `${finalData?.firstName} ${finalData?.lastName}`;
      debouncedUpdateTab({ route, title, replaceIfNotExist: true, shouldCloseDuplicate: true });
      navigate(route);
    },
    [
      param?.tab,
      param?.isDealsQueue,
      param?.recordNumber,
      param?.queueId,
      dealQueueLength,
      leadQueueLength,
      getQueueData,
      setCurrentRecord,
      debouncedUpdateTab,
      navigate,
      withQueryParams
    ]
  );

  useEffect(() => {
    if (contactData && !isContactDataLoading) {
      openTab({
        title: `${contactData.firstName} ${contactData.lastName}`,
        route: `${location.pathname}${location.search}`,
        replaceIfNotExist: true,
        shouldCloseDuplicate: true,
      });
    }
  }, [contactData, isContactDataLoading, location.pathname, location.search, openTab]);

  useEffect(() => {
    if (!firstLoad && contactData) {
      const title = `${contactData.firstName} ${contactData.lastName}`;

      updateCurrentTab({
        title,
        route: `${location.pathname.toString()}${location.search}`,
      });
    }
  }, [location.search, contactData, firstLoad, location.pathname]);

  const isLoading = useMemo(
    () => contactDetailsLoading || leadLoading || isContactDataLoading || loading,
    [contactDetailsLoading, leadLoading, isContactDataLoading, loading]
  );

  const reloadContactAlerts = () => {
    setContactAlertsKey((prevKey) => prevKey + 1);
  };

  const onOpenCreditReport = useCallback(
    async (bureauType: EBureauType, experianFicoTypeArray: CreditBureauScoreType[]) => {
      if (
        ((bureauType === EBureauType.Experian || bureauType === EBureauType.ExperianFico) && canPullExperian) ||
        (bureauType === EBureauType.Transunion && canPullTransunion) ||
        (bureauType === EBureauType.Equifax && canPullEquifax) ||
        canPullAll ||
        canViewAll
      ) {
        setCreditReports((prevState) => ({
          ...prevState,
          [bureauType]: (prevState[bureauType] || 0) + 1,
        }));
        setPulledFicoTypes((prevState) => ({
          ...prevState,
          [bureauType]: Array.from(new Set([...(prevState[bureauType] || []), ...experianFicoTypeArray])),
        }));
        reloadContactAlerts();
        return;
      }
      Notistack.generalError('You don\'t have permissions to view credit reports.');
    },
    [canPullAll, canPullExperian, canPullTransunion, canPullEquifax, canViewAll]
  );
  
  const onCloseCreditReport = useCallback((bureauType: string) => {
    setCreditReports((prevState) => {
      const newState = { ...prevState };
      delete newState[bureauType];
      return newState;
    });
  }, []);

  return (
    <ContactManagerWrapper>
      {creditReports[EBureauType.Experian] && (
        <Modal
          draggable
          hideBackdrop
          open
          title={EBureauType.Experian}
          dialogWidth="42%"
          dialogHeight="95%"
          resetPosition={creditReports[EBureauType.Experian]}
          onClose={() => onCloseCreditReport(EBureauType.Experian)}
        >
          <DetailedCreditReport
            bureauType={EBureauType.Experian}
            contactId={contactIdQuery}
            pulledFicoTypes={pulledFicoTypes[EBureauType.Experian] ?? []}
          />
        </Modal>
      )}

      {creditReports[EBureauType.Equifax] && (
        <Modal
          draggable
          hideBackdrop
          open
          title={EBureauType.Equifax}
          dialogWidth="42%"
          dialogHeight="95%"
          resetPosition={creditReports[EBureauType.Equifax]}
          onClose={() => onCloseCreditReport(EBureauType.Equifax)}
        >
          <DetailedCreditReport
            bureauType={EBureauType.Equifax}
            contactId={contactIdQuery}
            pulledFicoTypes={pulledFicoTypes[EBureauType.Equifax] ?? []}

          />
        </Modal>
      )}

      {creditReports[EBureauType.Transunion] && (
        <Modal
          draggable
          hideBackdrop
          open
          title={EBureauType.Transunion}
          dialogWidth="42%"
          dialogHeight="95%"
          resetPosition={creditReports[EBureauType.Transunion]}
          onClose={() => onCloseCreditReport(EBureauType.Transunion)}
        >
          <DetailedCreditReport
            bureauType={EBureauType.Transunion}
            contactId={contactIdQuery}
            reloadContact={reloadContact}
            pulledFicoTypes={pulledFicoTypes[EBureauType.Transunion] ?? []}

          />
        </Modal>
      )}

      <ContactManagerHeader
        currentTab={currentTab}
        contactId={contactIdQuery}
        leadId={selectedLeadId}
        assetId={assetIdQuery}
        dealId={dealIdQuery}
        contactDetailsData={contactDetailsData}
        leadData={leadData}
        dealSummary={deals}
        assets={assets}
        leads={leads}
        isLoading={isLoading}
        disableLeftBtn={currentRecord == 0 || disableArrowButtons}
        disableRightBtn={currentRecord == totalCount - 1 || disableArrowButtons}
        onOpenCreditReport={onOpenCreditReport}
        onArrowClick={handleArrowButtonClick}
      />

      <ContactManagerContentWraper container>
        <Grid
          item
          xs={10}
        >
          <PageWrapper
            direction="row"
            padding="0 20px 0"
          >
            <ContactAlerts count={contactAlertsKey} />
          </PageWrapper>
          <PageWrapper
            direction="row"
            padding="0 20px 0"
          >
            <ContactManagerTabs
              assets={assets}
              deal={deal}
              dealSummary={deals}
              selectLeadId={selectLeadId}
              selectedLeadId={selectedLeadId}
              displayAdditionalInfo={displayAdditionalInfo}
              reloadContactAlerts={reloadContactAlerts}
              contactData={contactData}
              reloadLeadData={reloadLeadData}
              reloadLeads={reloadLeads}
              reloadContact={reloadContact}
              reloadDeals={mutateDeals}
              reloadAssets={mutateAssets}
              validatingAssets={isAssetsLoading}
              validatingDeals={validatingDeals}
              dealsSummaryError={dealsSummaryError}
            />
          </PageWrapper>
        </Grid>
        <Grid
          item
          xs={2}
        >
          <SidebarWrapper>
            {currentTab === TabIndexEnum.Deals ? (
              <ContactManagerSidebar
                title="Deal Users"
                contactId={contactIdQuery}
              >
                <DealUsers
                  dealData={deal}
                  dealId={dealIdQuery}
                  userSelectOptions={userSelectOptions}
                  mutateDeal={mutateDeal}
                />
              </ContactManagerSidebar>
            ) : (
              <ContactUserBlock
                contactId={contactIdQuery}
                leadId={selectedLeadId}
                userSelectOptions={userSelectOptions}
                userSelectAllOptions={userSelectAllOptions}
              />
            )}
          </SidebarWrapper>
        </Grid>
      </ContactManagerContentWraper>
    </ContactManagerWrapper>
  );
};
